'use client';
import React, { useEffect, useState } from 'react';
import {
  Banner,
  Button,
  Status,
  Table,
  EmptyState,
  IconSticker,
} from '@yaradigitallabs/ahua-react';
import type { OutputMaterialListResponse } from '../../utils/match-maker.types';
import { MaterialStatusEnum } from '../../utils/match-maker.enums';
import { useOutputMaterialContext } from '../context';
import { getClassNames, replacePlaceholders } from '@/utils/helpers';
import { useNavigate } from 'react-router-dom';
import routes from '@/app/routes';
import { LoaderPlaceholder } from '@/components/Placeholder';
interface OutputMaterialTableProps {
  outputMaterialsData?: OutputMaterialListResponse;
  isLoading: boolean;
  error?: string | null;
}

const MaterialStatusDisplay: React.FC<{ status?: MaterialStatusEnum }> = ({
  status,
}) => {
  return (
    <div className="flex justify-start items-center">
      <Status
        colorType={status === MaterialStatusEnum.MATCHED ? 'green' : 'gray'}
        iconLeading={
          status === MaterialStatusEnum.MATCHED ? 'Check' : 'Skeleton'
        }
        size="n"
      >
        {status === MaterialStatusEnum.MATCHED ? 'Matched' : 'Not matched'}
      </Status>
    </div>
  );
};

const OutputMaterialTable: React.FC<OutputMaterialTableProps> = ({
  outputMaterialsData,
  isLoading,
  error,
}) => {
  const navigate = useNavigate();

  const handleSelect = (materialId: string) => {
    navigate(
      replacePlaceholders(routes.MATCH_MAKER_PRODUCTION_CONSUMPTION, {
        materialId,
      })
    );
  };
  const { searchQuery, showOnlyUnMatched } = useOutputMaterialContext();
  const [filteredData, setFilteredData] = useState<
    OutputMaterialListResponse['items']
  >([]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!outputMaterialsData) {
        setFilteredData([]);
        return;
      }

      const nextFilteredData = outputMaterialsData.items.filter(material => {
        const matchesSearchQuery =
          material.material_code
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          material.description
            .toLowerCase()
            .includes(searchQuery.toLowerCase());

        const matchesUnmatchedFilter = showOnlyUnMatched
          ? material.match_status !== MaterialStatusEnum.MATCHED
          : true;

        return matchesSearchQuery && matchesUnmatchedFilter;
      });

      setFilteredData(nextFilteredData);
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [searchQuery, showOnlyUnMatched, outputMaterialsData]);

  return (
    <div className="px-4 h-[calc(100vh-320px)] md:h-[calc(100vh-278px)] overflow-auto">
      <Table
        className={getClassNames(
          'w-full relative',
          !isLoading && (outputMaterialsData?.items || []).length === 0
            ? 'h-full'
            : ''
        )}
      >
        <thead className="sticky top-0 bg-white z-10 shadow-sm">
          <Table.Row>
            <Table.Head className="py-5! px-3! text-nowrap">
              Material number
            </Table.Head>
            <Table.Head className="py-5! px-3! text-nowrap">
              Material description
            </Table.Head>
            <Table.Head className="py-5! px-3! text-nowrap">
              Production line
            </Table.Head>
            <Table.Head className="py-5! px-3! text-nowrap">Status</Table.Head>
            <Table.Head className="py-5! px-3!"></Table.Head>
          </Table.Row>
        </thead>
        <tbody>
          {isLoading && (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <Table.Row className="border-b-0!" key={`loader_${index}`}>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                </Table.Row>
              ))}
            </>
          )}
          {error && (
            <Table.Row className="border-b-0!">
              <Table.Cell className="text-center py-6" colSpan={5}>
                <Banner colorConcept="destructiveLight" open>
                  <Banner.Middle>
                    <Banner.Body>
                      <Banner.Message size="n">
                        An error occurred while fetching produced materials.
                      </Banner.Message>
                    </Banner.Body>
                  </Banner.Middle>
                </Banner>
              </Table.Cell>
            </Table.Row>
          )}
          {!isLoading &&
            !error &&
            (outputMaterialsData?.items || []).length === 0 && (
              <Table.Row className="border-b-0!">
                <Table.Cell className="text-center py-6" colSpan={5}>
                  <EmptyState
                    captionText=""
                    iconStickerProps={{
                      icon: 'Info',
                      size: 'xl',
                    }}
                    titleText="No produced materials"
                  >
                    <EmptyState.Start>
                      <IconSticker icon="Info" size="xl" />
                    </EmptyState.Start>
                    <EmptyState.Middle>
                      <EmptyState.Title>No produced materials</EmptyState.Title>
                      <EmptyState.Caption>
                        We do not have any produced materials available for
                        matching. Please check back later.
                      </EmptyState.Caption>
                    </EmptyState.Middle>
                  </EmptyState>
                </Table.Cell>
              </Table.Row>
            )}
          {!isLoading &&
            !error &&
            filteredData.map(material => (
              <Table.Row key={material.id}>
                <Table.Cell className="leading-none! py-4! px-3!">
                  {material.material_code}
                </Table.Cell>
                <Table.Cell className="leading-none! py-4! px-3!">
                  {material.description}
                </Table.Cell>
                <Table.Cell className="leading-none! py-4! px-3!">
                  {material.facility_description ?? '-'}
                </Table.Cell>
                <Table.Cell className="leading-none! py-4! px-3!">
                  <MaterialStatusDisplay
                    status={material.match_status || MaterialStatusEnum.NONE}
                  />
                </Table.Cell>
                <Table.ActionsCell>
                  <div className="flex justify-end items-center">
                    <Button
                      colorConcept="brand"
                      size="s"
                      variant="ghost"
                      iconTrailing="ChevronRight"
                      onClick={() => handleSelect(material.id)}
                    >
                      Select
                    </Button>
                  </div>
                </Table.ActionsCell>
              </Table.Row>
            ))}
        </tbody>
      </Table>
    </div>
  );
};

export default OutputMaterialTable;
